// import moment from 'moment-timezone';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _reduce from 'lodash/reduce';
import _concat from 'lodash/concat';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _flatten from 'lodash/flatten';
import _groupBy from 'lodash/groupBy';
import _uniq from 'lodash/uniq';
import Vue from 'vue';
import emojiFlags from 'emoji-flags';
import { APImInterface } from './fetch';
import * as mutations from './mutation-types';
import { STATE } from './state';
import {
  actionFetch,
  actionFetchList,
  actionRequireObjectById,
  actionRequireObjects,
  fetcher,
  handleException,
} from './actions';
import { STANDARD } from './standard-types';
import countryCodes from '../../js/country-codes';
import { REGION_NAMES } from '../../js/locations';

const apimAPI = {
  namespaced: true,
  state: {
    ..._cloneDeep(STANDARD.state),
    agentInfo: {
      locations: {},
      locationsShort: {},
      regions: [],
      agents: {},
    },
    conformanceSettings: {
      settings: {},
      error: null,
    },
    aiModels: [],
    loading: {
      ..._cloneDeep(STANDARD.loadingState),
      agentInfo: STATE.NOT_SET,
      conformanceSettings: STATE.NOT_SET,
      aiModels: STATE.NOT_SET,
    },
    apiErrors: {},
    latestError: null,
  },
  getters: {
    // list{Object}s
    // get{Object}

    // Could use...STANDARD.getters, but better to be explicit:
    listAuths: STANDARD.getters.listAuths,
    getAuth: STANDARD.getters.getAuth,
    stateAuths: STANDARD.getters.stateAuths,
    stateAuth: STANDARD.getters.stateAuth,

    getBrowserTest: STANDARD.getters.getBrowserTest,
    stateBrowserTest: STANDARD.getters.stateBrowserTest,

    listCalls: STANDARD.getters.listCalls,
    getCall: STANDARD.getters.getCall,
    stateCalls: STANDARD.getters.stateCalls,
    stateCall: STANDARD.getters.stateCall,

    getAllTags(state, getters) {
      const sorter = (a, b) => {
        if (a.includes(':') && !b.includes(':')) {
          return 1;
        }
        if (!a.includes(':') && b.includes(':')) {
          return -1;
        }
        return a.localeCompare(b);
      };

      let output = [];
      if (state.loading.calls.ALL === STATE.SUCCESS) {
        const tagsArrs = getters.listCalls.map(({ meta }) => meta.tags || []);
        const tags = _flatten(tagsArrs);
        output = tags;
      }
      if (state.loading.workflows.ALL === STATE.SUCCESS) {
        const tagsArrs = getters.listWorkflows.map(({ meta }) => meta.tags || []);
        const tags = _flatten(tagsArrs);
        output = [...output, ...tags];
      }
      return _uniq(output).sort(sorter);
    },

    getAllFileTags(state, getters) {
      const sorter = (a, b) => a.localeCompare(b);
      let output = [];
      if (state.loading.files.ALL === STATE.SUCCESS) {
        const tagsArrs = getters.listFiles.map(({ meta }) => meta.tags || []);
        const tags = _flatten(tagsArrs);
        output = tags;
      }
      return _uniq(output).sort(sorter);
    },

    getConformanceSettings: (state) => {
      return state.conformanceSettings?.settings;
    },

    listDowntimes: STANDARD.getters.listDowntimes,
    getDowntime: STANDARD.getters.getDowntime,
    stateDowntimes: STANDARD.getters.stateDowntimes,
    stateDowntime: STANDARD.getters.stateDowntime,

    listEnvironments: STANDARD.getters.listEnvironments,
    getEnvironment: STANDARD.getters.getEnvironment,
    stateEnvironments: STANDARD.getters.stateEnvironments,
    stateEnvironment: STANDARD.getters.stateEnvironment,

    listFiles: STANDARD.getters.listFiles,
    getFile: STANDARD.getters.getFile,
    stateFiles: STANDARD.getters.stateFiles,
    stateFile: STANDARD.getters.stateFile,

    listJudges: STANDARD.getters.listJudges,
    getJudge: STANDARD.getters.getJudge,
    stateJudges: STANDARD.getters.stateJudges,
    stateJudge: STANDARD.getters.stateJudge,

    listReports: STANDARD.getters.listReports,
    getReport: STANDARD.getters.getReport,
    stateReports: STANDARD.getters.stateReports,
    stateReport: STANDARD.getters.stateReport,

    listSchedules: STANDARD.getters.listSchedules,
    getSchedule: STANDARD.getters.getSchedule,
    stateSchedules: STANDARD.getters.stateSchedules,
    stateSchedule: STANDARD.getters.stateSchedule,

    listSubscriptions: STANDARD.getters.listSubscriptions,
    getSubscription: STANDARD.getters.getSubscription,
    stateSubscriptions: STANDARD.getters.stateSubscriptions,
    stateSubscription: STANDARD.getters.stateSubscription,

    listTokens: STANDARD.getters.listTokens,
    getToken: STANDARD.getters.getToken,
    stateTokens: STANDARD.getters.stateTokens,
    stateToken: STANDARD.getters.stateToken,

    // listUsers: STANDARD.getters.listUsers,
    // getUser: STANDARD.getters.getUser,
    // stateUsers: STANDARD.getters.stateUsers,
    // stateUser: STANDARD.getters.stateUser,

    listWorkflows: STANDARD.getters.listWorkflows,
    getWorkflow: STANDARD.getters.getWorkflow,
    stateWorkflows: STANDARD.getters.stateWorkflows,
    stateWorkflow: STANDARD.getters.stateWorkflow,

    listAiModels: (state) => state.aiModels,

    getLocName: (state) => (locationId) => {
      if (state.loading.agentInfo === STATE.SUCCESS) {
        return _get(state.agentInfo.locations, locationId, locationId);
      }
      return locationId;
    },

    getLocNameShort: (state) => (locationId) => {
      if (state.loading.agentInfo === STATE.SUCCESS) {
        return _get(state.agentInfo.locationsShort, locationId, locationId);
      }
      return locationId;
    },

    scheduledTargets(state, getters) {
      const workflows = getters.listWorkflows || [];
      const targets = _reduce(
        getters.listSchedules,
        (targs, { schedule }) => _concat(targs, schedule.target_ids),
        [],
      );
      const scheduledWorkflows = _filter(workflows, (workflow) =>
        _find(targets, (targId) => targId === workflow.id),
      );
      const callsInWorkflows = _reduce(
        scheduledWorkflows,
        (targs, { workflow }) => _concat(targs, workflow.call_ids),
        [],
      );
      return _uniq(_concat(targets, callsInWorkflows));
    },

    userCalls(state, getters) {
      return (getters.listCalls || []).filter(({ meta }) => meta.workspace !== '_meta');
    },

    scheduledCalls(state, getters) {
      const calls = getters.userCalls.filter(({ id, meta }) => {
        const scheduled = !!_find(getters.scheduledTargets, (targ) => targ === id);
        const alwaysMonitored = meta.tags.indexOf('apimetrics:monitored') >= 0;
        const notMonitored = meta.tags.indexOf('apimetrics:not_monitored') >= 0;
        return alwaysMonitored || (!notMonitored && scheduled);
      });
      return calls;
    },

    scheduledWorkflows(state, getters) {
      const workflows = getters.listWorkflows.filter(({ id }) => {
        const scheduled = !!_find(getters.scheduledTargets, (targ) => targ === id);
        return scheduled;
      });
      return workflows;
    },

    // List getters - filter by other key
    getDowntimesBySchedule(state) {
      return ({ scheduleId }) =>
        Object.values(state.downtimes).filter(
          ({ meta }) => meta && meta.schedule_id === scheduleId,
        );
    },

    getSchedulesByCall(state, getters) {
      return ({ callId }) =>
        getters.listSchedules.filter((sch) => sch.schedule.target_ids.includes(callId));
    },
    getSchedulesByWorkflow(state, getters) {
      return ({ workflowId }) =>
        getters.listSchedules.filter((sch) => sch.schedule.target_ids.includes(workflowId));
    },
    getTokensByAuth(state, getters) {
      return ({ authId }) => getters.listTokens.filter(({ meta }) => meta.auth_id === authId);
    },
    getWorkflowsByCall(state, getters) {
      return ({ callId }) =>
        getters.listWorkflows.filter(({ workflow }) => workflow.call_ids.includes(callId));
    },

    // Item getters by ID

    getScheduledLocations(state, getters) {
      return ({ callId }) => {
        const workflows = getters.listWorkflows || [];
        const worksflowsForCall = _filter(workflows, ({ workflow }) =>
          _find(workflow.call_ids, (id) => id === callId),
        ).map(({ id }) => id);

        // Call Id + all workflow Ids that have that call
        const targets = [callId, ...worksflowsForCall];

        // Find schedules for any of these
        const schedules = _filter(getters.listSchedules || [], ({ schedule }) =>
          targets.reduce(
            (found, inId) => found || _find(schedule.target_ids, (id) => id === inId),
            false,
          ),
        );

        const info = schedules.reduce(
          (soFar, { schedule: { locations = [], regions = [], clouds = [] } }) => ({
            locations: [...soFar.locations, ...locations],
            regions: [...soFar.regions, ...regions],
            clouds: [...soFar.clouds, ...clouds],
          }),
          { locations: [], regions: [], clouds: [] },
        );

        const locations = _uniq(info.locations);
        const regions = _uniq(info.regions);
        const clouds = _uniq(info.clouds);

        const regionLocs = [...regions, ...clouds].reduce((soFar, region) => {
          const regInfo = _find(state.agentInfo.regions, ({ id }) => id === region);
          return [...soFar, ...(regInfo?.locations || [])];
        }, []);

        return _uniq([...locations, ...regionLocs]);
      };
    },
  },
  mutations: {
    CLEAR_CACHE: (state) => {
      Object.entries(STANDARD.state).forEach(([key, val]) => {
        Vue.set(state, key, _cloneDeep(val));
      });
      Object.entries(STANDARD.loadingState).forEach(([key, val]) => {
        Vue.set(state.loading, key, _cloneDeep(val));
      });
      Vue.set(state.conformanceSettings, 'settings', {});
      Vue.set(state.loading, 'conformanceSettings', STATE.NOT_SET);
    },

    [mutations.STORE_ERROR]: (state, { name, error }) => {
      Vue.set(state.apiErrors, name, error);
      Vue.set(state, 'latestError', error);
    },
    [mutations.CLEAR_ERRORS]: (state) => {
      state.apiErrors = {};
      Vue.set(state, 'latestError', null);
    },

    // STORE_{OBJECT}S
    // SAVE_{OBJECT}
    // Could use...STANDARD.mutations, but explicit is better than implicit
    [mutations.STORE_AUTHS]: STANDARD.mutations[mutations.STORE_AUTHS],
    [mutations.SAVE_AUTH]: STANDARD.mutations[mutations.SAVE_AUTH],
    [mutations.STORE_BROWSER_TESTS]: STANDARD.mutations[mutations.STORE_BROWSER_TESTS],
    [mutations.SAVE_BROWSER_TEST]: STANDARD.mutations[mutations.SAVE_BROWSER_TEST],
    [mutations.STORE_CALLS]: STANDARD.mutations[mutations.STORE_CALLS],
    [mutations.STORE_MCP_TESTS]: STANDARD.mutations[mutations.STORE_MCP_TESTS],
    [mutations.SAVE_MCP_TEST]: STANDARD.mutations[mutations.SAVE_MCP_TEST],
    [mutations.SAVE_CALL]: STANDARD.mutations[mutations.SAVE_CALL],
    [mutations.STORE_DOWNTIMES]: STANDARD.mutations[mutations.STORE_DOWNTIMES],
    [mutations.SAVE_DOWNTIME]: STANDARD.mutations[mutations.SAVE_DOWNTIME],
    [mutations.STORE_ENVIRONMENTS]: STANDARD.mutations[mutations.STORE_ENVIRONMENTS],
    [mutations.SAVE_ENVIRONMENT]: STANDARD.mutations[mutations.SAVE_ENVIRONMENT],
    [mutations.STORE_FILES]: STANDARD.mutations[mutations.STORE_FILES],
    [mutations.SAVE_FILE]: STANDARD.mutations[mutations.SAVE_FILE],
    [mutations.STORE_JUDGES]: STANDARD.mutations[mutations.STORE_JUDGES],
    [mutations.SAVE_JUDGE]: STANDARD.mutations[mutations.SAVE_JUDGE],
    [mutations.STORE_REPORTS]: STANDARD.mutations[mutations.STORE_REPORTS],
    [mutations.SAVE_REPORT]: STANDARD.mutations[mutations.SAVE_REPORT],
    [mutations.STORE_SCHEDULES]: STANDARD.mutations[mutations.STORE_SCHEDULES],
    [mutations.SAVE_SCHEDULE]: STANDARD.mutations[mutations.SAVE_SCHEDULE],
    [mutations.STORE_SUBSCRIPTIONS]: STANDARD.mutations[mutations.STORE_SUBSCRIPTIONS],
    [mutations.SAVE_SUBSCRIPTION]: STANDARD.mutations[mutations.SAVE_SUBSCRIPTION],
    [mutations.STORE_TOKENS]: STANDARD.mutations[mutations.STORE_TOKENS],
    [mutations.SAVE_TOKEN]: STANDARD.mutations[mutations.SAVE_TOKEN],
    // [mutations.STORE_USERS]: STANDARD.mutations[mutations.STORE_USERS],
    // [mutations.SAVE_USER]: STANDARD.mutations[mutations.SAVE_USER],
    [mutations.STORE_WORKFLOWS]: STANDARD.mutations[mutations.STORE_WORKFLOWS],
    [mutations.SAVE_WORKFLOW]: STANDARD.mutations[mutations.SAVE_WORKFLOW],

    [mutations.STORE_CONFORMANCE_SETTINGS]: (state, { status, data, error }) => {
      state.loading.conformanceSettings = status;
      if (status === STATE.FAILED) {
        Vue.set(state.conformanceSettings, 'error', error);
      } else if (status === STATE.SUCCESS) {
        Vue.set(state.conformanceSettings, 'settings', data);
      }
    },

    SAVE_AGENT_INFO: (state, { status, data, error }) => {
      state.loading.agentInfo = status;
      if (status === STATE.FAILED) {
        Vue.set(state.agentInfo, 'error', error);
      } else if (status === STATE.SUCCESS) {
        // New project-based endpoint returns API v2 format with regions, locations, agent_list
        const agentList = data.agent_list || [];
        const locations = data.locations || {};

        // Add country and continent regions
        const toRegion =
          (type) =>
          ([regionCode, agents]) => {
            const regionCodeUpper = regionCode.toUpperCase();
            return {
              id: `${type}:${regionCodeUpper}`,
              locations: agents.map(({ id }) => id),
              name:
                type === 'Continent'
                  ? REGION_NAMES[`Continent:${regionCodeUpper}`] || regionCodeUpper
                  : countryCodes.find(({ id }) => id === regionCodeUpper)?.text || regionCodeUpper,
            };
          };

        const agentsByCountry = _groupBy(agentList, (agent) => agent.agent.country);
        const agentsByContinent = _groupBy(agentList, (agent) => agent.agent.continent);
        const countries = Object.entries(agentsByCountry).map(toRegion('Country'));
        const continents = Object.entries(agentsByContinent).map(toRegion('Continent'));
        const regions = [...(data.regions || []), ...countries, ...continents];

        // Build agents object keyed by ID
        const agents = agentList.reduce((acc, agent) => {
          acc[agent.id] = agent;
          return acc;
        }, {});

        // Build short location names with emoji flags
        const locationsShort = agentList.reduce((acc, obj) => {
          const { agent } = obj;
          const info = agent.country ? emojiFlags.countryCode(agent.country) : null;
          const region = agent.region && agent.region !== '?' ? `${agent.region} ` : '';
          if (info) {
            const { emoji, name } = info;
            acc[obj.id] = `${agent.cloud} ${region}${emoji} ${name}`;
          } else {
            acc[obj.id] = `${agent.cloud} ${region}${agent.country}`;
          }
          return acc;
        }, {});

        Vue.set(state.agentInfo, 'regions', regions);
        Vue.set(state.agentInfo, 'locations', locations);
        Vue.set(state.agentInfo, 'agents', agents);
        Vue.set(state.agentInfo, 'locationsShort', locationsShort);
      }
    },

    CLEAR_AGENT_INFO: (state) => {
      state.loading.agentInfo = STATE.NOT_SET;
      state.agentInfo = {};
    },

    SAVE_AI_MODELS: (state, { status, data }) => {
      state.loading.aiModels = status;
      if (status === STATE.SUCCESS) {
        state.aiModels = data?.results || [];
      }
    },
  },
  actions: {
    resetApiCache({ commit }) {
      commit('CLEAR_CACHE');
    },
    storeAPIError({ commit }, { name, error }) {
      commit(mutations.STORE_ERROR, { name, error });
    },
    clearAPIErrors({ commit }) {
      commit(mutations.CLEAR_ERRORS, {});
    },

    // fetch{Object}s
    // fetch{Object}
    // create{Object}
    // update{Object}
    // delete{Object}
    // require{Object}s
    // require{Object}

    // Could use ...STANDARD.actions here, but explicit is better than implicit
    fetchAuths: STANDARD.actions.fetchAuths,
    fetchAuth: STANDARD.actions.fetchAuth,
    createAuth: STANDARD.actions.createAuth,
    updateAuth: STANDARD.actions.updateAuth,
    deleteAuth: STANDARD.actions.deleteAuth,
    requireAuths: STANDARD.actions.requireAuths,
    requireAuth: STANDARD.actions.requireAuth,

    fetchBrowserTest: STANDARD.actions.fetchBrowserTest,
    createBrowserTest: async ({ commit }, params) => {
      // Call the browser test create API
      const result = await fetcher[APImInterface.CREATE.BROWSERTEST](params);

      // Update both caches to keep them in sync
      // 1. Update calls cache (for CallsHome list) - transform response to have request structure
      const transformedForCalls =
        result && result.setup
          ? {
              ...result,
              request: { url: result.setup.url },
            }
          : result;
      commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: transformedForCalls });

      // 2. Update browserTests cache (for edit page) - keep original structure
      commit(mutations.SAVE_BROWSER_TEST, { status: 'SUCCESS', params, data: result });

      return result;
    },
    updateBrowserTest: async ({ commit }, params) => {
      // Call the browser test update API
      const result = await fetcher[APImInterface.UPDATE.BROWSERTEST](params);

      // Update both caches to keep them in sync
      // 1. Update calls cache (for CallsHome list) - transform response to have request structure
      const transformedForCalls =
        result && result.setup
          ? {
              ...result,
              request: { url: result.setup.url },
            }
          : result;
      commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: transformedForCalls });

      // 2. Update browserTests cache (for edit page) - keep original structure
      commit(mutations.SAVE_BROWSER_TEST, { status: 'SUCCESS', params, data: result });

      return result;
    },
    deleteBrowserTest: async ({ commit }, params) => {
      // Call the browser test delete API
      const result = await fetcher[APImInterface.DELETE.BROWSERTEST](params);

      // Update both caches to remove the deleted item
      // 1. Update calls cache (for CallsHome list)
      commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: undefined });

      // 2. Update browserTests cache (for edit page)
      commit(mutations.SAVE_BROWSER_TEST, { status: 'SUCCESS', params, data: undefined });

      return result;
    },
    requireBrowserTest: STANDARD.actions.requireBrowserTest,

    fetchMcpTest: STANDARD.actions.fetchMcpTest,
    createMcpTest: async ({ commit }, params) => {
      // Call the MCP test create API
      const result = await fetcher[APImInterface.CREATE.MCPTEST](params);

      // Update both caches to keep them in sync
      // 1. Update calls cache (for CallsHome list) - transform response to have request structure
      const transformedForCalls = result
        ? {
            ...result,
            request: { url: result.setup?.url },
            meta: {
              ...result.meta,
              monitor_type: 'mcp',
            },
          }
        : result;
      commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: transformedForCalls });

      // 2. Update mcpTests cache (for edit page) - keep original structure
      commit(mutations.SAVE_MCP_TEST, { status: 'SUCCESS', params, data: result });

      return result;
    },
    updateMcpTest: async ({ commit }, params) => {
      // Call the MCP test update API
      const result = await fetcher[APImInterface.UPDATE.MCPTEST](params);

      // Update both caches to keep them in sync
      // 1. Update calls cache (for CallsHome list) - transform response to have request structure
      const transformedForCalls = result
        ? {
            ...result,
            request: { url: result.setup?.url },
            meta: {
              ...result.meta,
              monitor_type: 'mcp',
            },
          }
        : result;
      commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: transformedForCalls });

      // 2. Update mcpTests cache (for edit page) - keep original structure
      commit(mutations.SAVE_MCP_TEST, { status: 'SUCCESS', params, data: result });

      return result;
    },
    deleteMcpTest: async ({ commit }, params) => {
      // Call the MCP test delete API
      const result = await fetcher[APImInterface.DELETE.MCPTEST](params);

      // Update both caches to remove the deleted item
      // 1. Update calls cache (for CallsHome list)
      commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: undefined });

      // 2. Update mcpTests cache (for edit page)
      commit(mutations.SAVE_MCP_TEST, { status: 'SUCCESS', params, data: undefined });

      return result;
    },
    requireMcpTest: STANDARD.actions.requireMcpTest,

    fetchCalls: STANDARD.actions.fetchCalls,
    fetchCall: STANDARD.actions.fetchCall,
    createCall: STANDARD.actions.createCall,
    /*
     * Errors are not swallowed here, unlike the STANDARD create actions: the
     * sidebar reads the backend's validation detail straight off the
     * exception, and there is no per-monitor state to record because the
     * request is all-or-nothing.
     */
    createMonitorSet: async ({ commit }, params) => {
      const result = await fetcher.createMonitorSet(params);
      (result?.results || []).forEach((monitor) => {
        commit(mutations.SAVE_CALL, { status: 'SUCCESS', params, data: monitor });
      });
      return result;
    },
    updateCall: STANDARD.actions.updateCall,
    deleteCall: STANDARD.actions.deleteCall,
    requireCalls: STANDARD.actions.requireCalls,
    requireCall: STANDARD.actions.requireCall,

    requireConformanceSettings: async ({ commit }) => {
      const mutation = mutations.STORE_CONFORMANCE_SETTINGS;
      commit(mutation, { status: STATE.PENDING });
      const data = await fetcher.getConformanceSettings().catch(async (except) => {
        if (except.name !== 'AbortError') {
          const error = await handleException(except);
          commit(mutation, { status: STATE.FAILED });
          commit(mutations.STORE_ERROR, { name: 'getConformanceSettings', error });
        }
      });
      commit(mutation, { status: STATE.SUCCESS, data });
    },

    updateConformanceSettings: async ({ commit }, data) => {
      const mutation = mutations.STORE_CONFORMANCE_SETTINGS;
      commit(mutation, { status: STATE.PENDING });
      await fetcher.updateConformanceSettings({ data }).catch(async (except) => {
        if (except.name !== 'AbortError') {
          const error = await handleException(except);
          commit(mutation, { status: STATE.FAILED });
          commit(mutations.STORE_ERROR, { name: 'updateConformanceSettings', error });
        }
      });
      commit(mutation, { status: STATE.SUCCESS, data });
    },

    // fetchDowntimes: STANDARD.actions.fetchDowntimes,
    fetchDowntime: STANDARD.actions.fetchDowntime,
    createDowntime: STANDARD.actions.createDowntime,
    updateDowntime: STANDARD.actions.updateDowntime,
    deleteDowntime: STANDARD.actions.deleteDowntime,
    // requireDowntimes: STANDARD.actions.requireDowntimes,
    // requireDowntime: STANDARD.actions.requireDowntime,

    fetchEnvironments: STANDARD.actions.fetchEnvironments,
    fetchEnvironment: STANDARD.actions.fetchEnvironment,
    createEnvironment: STANDARD.actions.createEnvironment,
    updateEnvironment: STANDARD.actions.updateEnvironment,
    deleteEnvironment: STANDARD.actions.deleteEnvironment,
    requireEnvironments: STANDARD.actions.requireEnvironments,
    requireEnvironment: STANDARD.actions.requireEnvironment,

    fetchFiles: STANDARD.actions.fetchFiles,
    fetchFile: STANDARD.actions.fetchFile,
    createFile: STANDARD.actions.createFile,
    updateFile: STANDARD.actions.updateFile,
    deleteFile: STANDARD.actions.deleteFile,
    requireFiles: STANDARD.actions.requireFiles,
    requireFile: STANDARD.actions.requireFile,

    // Judges are read-only in the API so far: no create, update or delete actions.
    fetchJudges: STANDARD.actions.fetchJudges,
    fetchJudge: STANDARD.actions.fetchJudge,
    requireJudges: STANDARD.actions.requireJudges,
    requireJudge: STANDARD.actions.requireJudge,

    fetchReports: STANDARD.actions.fetchReports,
    fetchReport: STANDARD.actions.fetchReport,
    createReport: STANDARD.actions.createReport,
    updateReport: STANDARD.actions.updateReport,
    deleteReport: STANDARD.actions.deleteReport,
    requireReports: STANDARD.actions.requireReports,
    requireReport: STANDARD.actions.requireReport,

    fetchSchedules: STANDARD.actions.fetchSchedules,
    fetchSchedule: STANDARD.actions.fetchSchedule,
    createSchedule: STANDARD.actions.createSchedule,
    updateSchedule: STANDARD.actions.updateSchedule,
    deleteSchedule: STANDARD.actions.deleteSchedule,
    requireSchedules: STANDARD.actions.requireSchedules,
    requireSchedule: STANDARD.actions.requireSchedule,

    fetchSubscriptions: STANDARD.actions.fetchSubscriptions,
    fetchSubscription: STANDARD.actions.fetchSubscription,
    createSubscription: STANDARD.actions.createSubscription,
    updateSubscription: STANDARD.actions.updateSubscription,
    deleteSubscription: STANDARD.actions.deleteSubscription,
    requireSubscriptions: STANDARD.actions.requireSubscriptions,
    requireSubscription: STANDARD.actions.requireSubscription,

    fetchTokens: STANDARD.actions.fetchTokens,
    fetchToken: STANDARD.actions.fetchToken,
    createToken: STANDARD.actions.createToken,
    updateToken: STANDARD.actions.updateToken,
    deleteToken: STANDARD.actions.deleteToken,
    requireTokens: STANDARD.actions.requireTokens,
    requireToken: STANDARD.actions.requireToken,

    // fetchUsers: STANDARD.actions.fetchUsers,
    // fetchUser: STANDARD.actions.fetchUser,
    // createUser: STANDARD.actions.createUser,
    // updateUser: STANDARD.actions.updateUser,
    // deleteUser: STANDARD.actions.deleteUser,
    // requireUsers: STANDARD.actions.requireUsers,
    // requireUser: STANDARD.actions.requireUser,

    fetchWorkflows: STANDARD.actions.fetchWorkflows,
    fetchWorkflow: STANDARD.actions.fetchWorkflow,
    createWorkflow: STANDARD.actions.createWorkflow,
    updateWorkflow: STANDARD.actions.updateWorkflow,
    deleteWorkflow: STANDARD.actions.deleteWorkflow,
    requireWorkflows: STANDARD.actions.requireWorkflows,
    requireWorkflow: STANDARD.actions.requireWorkflow,

    requireMonitoredCalls: async ({ dispatch }) => {
      const futs = [
        dispatch('requireCalls'),
        dispatch('requireWorkflows'),
        dispatch('requireSchedules'),
      ];
      return Promise.all(futs);
    },

    requireMonitoredWorkflows: async ({ dispatch }) => {
      const futs = [dispatch('requireWorkflows'), dispatch('requireSchedules')];
      return Promise.all(futs);
    },

    requireAgentInfo: async ({ state, dispatch }) => {
      const path = 'agentInfo';
      const currState = _get(state.loading, path, STATE.NOT_SET);

      if (currState === STATE.NOT_SET) {
        await dispatch('fetchAgentInfo');
      }
      // No need for a getter for a simple state like this
      return state.agentInfo;
    },
    fetchAgentInfo: async ({ commit, rootState }) => {
      const mutation = 'SAVE_AGENT_INFO';
      commit(mutation, { status: STATE.PENDING });
      const { projectId } = rootState.account2;
      if (!projectId) {
        commit(mutation, { status: STATE.FAILED, error: 'No project selected' });
        return;
      }
      const data = await fetcher.getAgentInfo().catch(async (except) => {
        if (except.name !== 'AbortError') {
          const error = await handleException(except);
          commit(mutation, { status: STATE.FAILED });
          commit(mutations.STORE_ERROR, { name: 'getAgentInfo', error });
        }
      });
      if (data) {
        commit(mutation, { status: STATE.SUCCESS, data });
      }
    },

    clearAgentInfo({ commit }) {
      commit('CLEAR_AGENT_INFO');
    },

    /*
     * The AI model catalogue, fetched at most once per session. The list is
     * global and changes rarely (the backend caches it in turn), so re-fetching
     * on every sidebar open would be wasted work.
     *
     * A failure is recorded and swallowed: callers fall back to their own
     * seed list, so an unreachable endpoint must not surface as an error.
     */
    requireAiModels: async ({ commit, state }) => {
      // PENDING is skipped as well as SUCCESS: the sidebar calls this every
      // time it opens, so without it closing and reopening while the first
      // request is still in flight fires a second one.
      const loaded = state.loading.aiModels;
      if (loaded === STATE.SUCCESS || loaded === STATE.PENDING) {
        return;
      }
      const mutation = 'SAVE_AI_MODELS';
      commit(mutation, { status: STATE.PENDING });
      const data = await fetcher.getAiModels().catch((except) => {
        if (except.name === 'AbortError') {
          // Back to NOT_SET rather than left at PENDING: an abort (a project
          // switch, say) completes no request, and the guard above skips
          // PENDING, so leaving it set would strand the catalogue on the seed
          // list until the page is reloaded.
          commit(mutation, { status: STATE.NOT_SET });
          return;
        }
        // Deliberately not STORE_ERROR: that feeds `apiErrors`, which App.vue
        // renders as a global danger alert. Callers fall back to their own
        // seed list, so this failure is recoverable and must stay quiet.
        console.debug('AI model catalogue unavailable, using seed list', except);
        commit(mutation, { status: STATE.FAILED });
      });
      if (data) {
        commit(mutation, { status: STATE.SUCCESS, data });
      }
    },

    //
    // Get a List of Objects
    //

    fetchSchedulesByCall: actionFetchList(
      mutations.STORE_SCHEDULES,
      APImInterface.QUERY.SCHEDULES_BY_CALL,
    ),
    requireSchedulesByCall: actionRequireObjects(
      ({ callId }) => ['schedules', callId],
      'fetchSchedulesByCall',
      'getSchedulesByCall',
    ),

    fetchSchedulesByWorkflow: actionFetchList(
      mutations.STORE_SCHEDULES,
      APImInterface.QUERY.SCHEDULES_BY_WORKFLOW,
    ),
    requireSchedulesByWorkflow: actionRequireObjects(
      ({ workflowId }) => ['schedules', workflowId],
      'fetchSchedulesByWorkflow',
      'getSchedulesByWorkflow',
    ),

    fetchTokensByAuth: actionFetchList(mutations.STORE_TOKENS, APImInterface.QUERY.TOKENS_BY_AUTH),
    requireTokensByAuth: actionRequireObjects(
      ({ authId }) => ['tokens', authId],
      'fetchTokensByAuth',
      'getTokensByAuth',
    ),

    fetchWorkflowsByCall: actionFetchList(
      mutations.STORE_WORKFLOWS,
      APImInterface.QUERY.WORKFLOWS_BY_CALL,
    ),
    requireWorkflowsByCall: actionRequireObjects(
      ({ callId }) => ['workflows', callId],
      'fetchWorkflowsByCall',
      'getWorkflowsByCall',
    ),

    fetchDowntimesBySchedule: actionFetchList(
      mutations.STORE_DOWNTIMES,
      APImInterface.QUERY.DOWNTIMES,
    ),
    requireDowntimesBySchedule: actionRequireObjectById(
      'downtimes',
      'scheduleId',
      'fetchDowntimesBySchedule',
      'getDowntimesBySchedule',
    ),

    addCallToSchedule: actionFetch(mutations.SAVE_SCHEDULE, 'addCallToSchedule'),
    removeCallFromSchedule: actionFetch(mutations.SAVE_SCHEDULE, 'removeCallFromSchedule'),

    addWorkflowToSchedule: actionFetch(mutations.SAVE_SCHEDULE, 'addWorkflowToSchedule'),
    removeWorkflowFromSchedule: actionFetch(mutations.SAVE_SCHEDULE, 'removeWorkflowFromSchedule'),

    uploadOas: actionFetch(mutations.SAVE_FILE, 'uploadOas'),
    uploadOasUrl: actionFetch(mutations.SAVE_FILE, 'uploadOasUrl'),
  },
};

export { apimAPI, fetcher, STATE, handleException };
